import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGuardrailsScanDetails } from '../../../services/cloudopsApi';
import { useClient } from '../../../contexts/ClientContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  FiArrowLeft as ArrowLeft,
  FiShield as Shield,
  FiCheckCircle as CheckCircle2,
  FiXCircle as XCircle,
  FiAlertTriangle as AlertTriangle,
  FiInfo as Info,
  FiServer as Server,
  FiFilter as Filter,
  FiChevronDown as ChevronDown,
  FiChevronRight as ChevronRight,
  FiEye as Eye,
} from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import ErrorDisplay from '../../../components/ErrorDisplay';
import EvaluationDetailsModal from '../components/EvaluationDetailsModal';

interface GuardrailApplied {
  guardrail_id: string;
  guardrail_name: string;
  status: string;
  severity: string;
  category: string;
  violation_message?: string;
  detected_at?: string;
  violation_id?: string;
}

interface ScanResource {
  resource_id: string;
  resource_type: string;
  resource_region: string;
  resource_arn?: string;
  guardrails_applied: GuardrailApplied[];
}

interface ScanInfo {
  id: string;
  status: string;
  scan_type: string;
  started_at: string;
  completed_at?: string;
  error_message?: string;
}

interface ScanSummary {
  compliance_rate: number;
  total_resources_scanned: number;
  total_invalid_findings: number;
  total_guardrails_applied: number;
  violations_by_severity?: Record<string, number>;
}

interface ScanDetailsData {
  scan: ScanInfo;
  summary: ScanSummary;
  resources: ScanResource[];
}

interface EvaluationModalData {
  scanId: string | undefined;
  resourceId: string;
  guardrailId: string;
  guardrailName: string;
}

const ScanDetailsTimelinePage: React.FC = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const { selectedClientId, isLoadingClients } = useClient();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scanDetails, setScanDetails] = useState<ScanDetailsData | null>(null);

  // Filter and sort state
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // all, valid, invalid
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>('type'); // type, violations, region
  const [evaluationModal, setEvaluationModal] = useState<EvaluationModalData | null>(null);

  useEffect(() => {
    if (scanId) {
      // Avoid calling scan details endpoints without client_id for partner/aws users.
      const needsClientId = isAWSUser() ||
        hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
        hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
        hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
        hasPermission(PERMISSIONS.PARTNERS_MANAGE);
      if (needsClientId && !selectedClientId) {
        if (isLoadingClients) return;
        setLoading(false);
        setError('Please select a client to view scan details.');
        return;
      }
      loadScanDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId, selectedClientId, isLoadingClients, hasPermission, isAWSUser]);

  const loadScanDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getGuardrailsScanDetails(scanId!);
      setScanDetails(data);
      
      // Expand first few resources by default
      if (data?.resources && data.resources.length > 0) {
        const firstFew = new Set<string>(data.resources.slice(0, 3).map((r: ScanResource) => r.resource_id));
        setExpandedResources(firstFew);
      }
    } catch (err: unknown) {
      console.error('Error loading scan details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scan details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (startedAt: string | null | undefined, completedAt: string | null | undefined): string => {
    if (!startedAt) return 'N/A';
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getScanStatusColor = (status: string | undefined): string => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'in_progress':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'pending':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getSeverityColor = (severity: string | undefined): string => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getCategoryColor = (category: string | undefined): string => {
    switch (category?.toLowerCase()) {
      case 'security':
        return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300';
      case 'cost':
        return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
      case 'governance':
        return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300';
      case 'reliability':
        return 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300';
      default:
        return 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const toggleResourceExpansion = (resourceId: string) => {
    const newExpanded = new Set(expandedResources);
    if (newExpanded.has(resourceId)) {
      newExpanded.delete(resourceId);
    } else {
      newExpanded.add(resourceId);
    }
    setExpandedResources(newExpanded);
  };

  // Filter and sort resources
  const filteredAndSortedResources = useMemo(() => {
    if (!scanDetails?.resources) return [];

    let filtered = scanDetails.resources.filter((resource) => {
      // Resource type filter
      if (resourceTypeFilter !== 'all' && resource.resource_type !== resourceTypeFilter) {
        return false;
      }

      // Status filter (check if resource has any invalid guardrails)
      if (statusFilter === 'invalid') {
        const hasInvalid = resource.guardrails_applied.some(g => g.status === 'invalid');
        if (!hasInvalid) return false;
      } else if (statusFilter === 'valid') {
        const allValid = resource.guardrails_applied.every(g => g.status === 'valid');
        if (!allValid) return false;
      }

      // Severity filter
      if (severityFilter !== 'all') {
        const hasMatchingSeverity = resource.guardrails_applied.some(
          g => g.status === 'invalid' && g.severity === severityFilter
        );
        if (!hasMatchingSeverity) return false;
      }

      // Category filter
      if (categoryFilter !== 'all') {
        const hasMatchingCategory = resource.guardrails_applied.some(
          g => g.category === categoryFilter
        );
        if (!hasMatchingCategory) return false;
      }

      return true;
    });

    // Sort resources
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'type':
          return a.resource_type.localeCompare(b.resource_type);
        case 'violations': {
          const aViolations = a.guardrails_applied.filter(g => g.status === 'invalid').length;
          const bViolations = b.guardrails_applied.filter(g => g.status === 'invalid').length;
          return bViolations - aViolations;
        }
        case 'region':
          return a.resource_region.localeCompare(b.resource_region);
        default:
          return 0;
      }
    });

    return filtered;
  }, [scanDetails, resourceTypeFilter, statusFilter, severityFilter, categoryFilter, sortBy]);

  // Get unique resource types, severities, and categories for filters
  const uniqueResourceTypes = useMemo(() => {
    if (!scanDetails?.resources) return [];
    return Array.from(new Set(scanDetails.resources.map((r: ScanResource) => r.resource_type))).sort((a: string, b: string) => a.localeCompare(b));
  }, [scanDetails]);

  const uniqueSeverities = useMemo(() => {
    if (!scanDetails?.resources) return [];
    const severities = new Set<string>();
    scanDetails.resources.forEach((r: ScanResource) => {
      r.guardrails_applied.forEach((g: GuardrailApplied) => {
        if (g.status === 'invalid') severities.add(g.severity);
      });
    });
    return ['critical', 'high', 'medium', 'low'].filter(s => severities.has(s));
  }, [scanDetails]);

  const uniqueCategories = useMemo(() => {
    if (!scanDetails?.resources) return [];
    return Array.from(new Set(scanDetails.resources.flatMap((r: ScanResource) => r.guardrails_applied.map((g: GuardrailApplied) => g.category)))).sort((a: string, b: string) => a.localeCompare(b));
  }, [scanDetails]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !scanDetails) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorDisplay
          error={error || 'Scan details not found'}
          onRetry={loadScanDetails}
          title="Failed to Load Scan Details"
        />
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mt-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const { scan, summary } = scanDetails;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Scan Details Timeline
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Scan ID: {scan.id}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scan Header Section */}
      <div className="mb-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Scan Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</p>
                <span className={`inline-block mt-1 px-2 py-1 text-xs font-semibold rounded-full ${getScanStatusColor(scan.status)}`}>
                  {scan.status}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Scan Type</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{scan.scan_type}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Started</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(scan.started_at)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Duration</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDuration(scan.started_at, scan.completed_at)}</p>
              </div>
            </div>
            {scan.error_message && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-800 dark:text-red-200">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  {scan.error_message}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Compliance Rate</p>
                  <p className={`text-3xl font-bold mt-2 ${
                    summary.compliance_rate >= 95
                      ? 'text-green-600 dark:text-green-400'
                      : summary.compliance_rate >= 80
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {summary.compliance_rate.toFixed(2)}%
                  </p>
                </div>
                <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resources Scanned</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {summary.total_resources_scanned}
                  </p>
                </div>
                <Server className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Violations</p>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                    {summary.total_invalid_findings}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Guardrails Applied</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {summary.total_guardrails_applied}
                  </p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Violations by Severity */}
        {summary.violations_by_severity && Object.keys(summary.violations_by_severity).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Violations by Severity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(summary.violations_by_severity).map(([severity, count]) => (
                  <div key={severity} className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getSeverityColor(severity)}`}>
                      {severity.charAt(0).toUpperCase() + severity.slice(1)}: {count}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            <CardTitle>Filters & Sorting</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select
              label="Resource Type"
              value={resourceTypeFilter}
              onChange={(e) => setResourceTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                ...uniqueResourceTypes.map(type => ({ value: type, label: type.toUpperCase() })),
              ]}
            />
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'valid', label: 'Valid Only' },
                { value: 'invalid', label: 'With Violations' },
              ]}
            />
            <Select
              label="Severity"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Severities' },
                ...uniqueSeverities.map(severity => ({
                  value: severity,
                  label: severity.charAt(0).toUpperCase() + severity.slice(1),
                })),
              ]}
            />
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Categories' },
                ...uniqueCategories.map(category => ({
                  value: category,
                  label: category.charAt(0).toUpperCase() + category.slice(1),
                })),
              ]}
            />
            <Select
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              options={[
                { value: 'type', label: 'Resource Type' },
                { value: 'violations', label: 'Violation Count' },
                { value: 'region', label: 'Region' },
              ]}
            />
          </div>
        </CardContent>
      </Card>

      {/* Resources Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>
            Resources Timeline ({filteredAndSortedResources.length} of {scanDetails.resources?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAndSortedResources.length > 0 ? (
            <div className="space-y-4">
              {filteredAndSortedResources.map((resource) => {
                const isExpanded = expandedResources.has(resource.resource_id);
                const violationCount = resource.guardrails_applied.filter(g => g.status === 'invalid').length;
                const validCount = resource.guardrails_applied.filter(g => g.status === 'valid').length;

                return (
                  <div
                    key={resource.resource_id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    <div
                      className="p-4 bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => toggleResourceExpansion(resource.resource_id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleResourceExpansion(resource.resource_id); }} role="button" tabIndex={0}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          )}
                          <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 uppercase">
                            {resource.resource_type}
                          </span>
                          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                            {resource.resource_region}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {resource.resource_arn || resource.resource_id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 ml-4">
                          {violationCount > 0 && (
                            <span className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                              <XCircle className="w-4 h-4" />
                              {violationCount}
                            </span>
                          )}
                          {validCount > 0 && (
                            <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                              <CheckCircle2 className="w-4 h-4" />
                              {validCount}
                            </span>
                          )}
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {resource.guardrails_applied.length} guardrails
                          </span>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-3">
                          {resource.guardrails_applied.map((guardrail) => {
                            const isInvalid = guardrail.status === 'invalid';

                            return (
                              <div
                                key={guardrail.guardrail_id}
                                className={`p-3 rounded-lg border ${
                                  isInvalid
                                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-2">
                                      {isInvalid ? (
                                        <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                      ) : (
                                        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                                      )}
                                      <span className="font-medium text-gray-900 dark:text-white">
                                        {guardrail.guardrail_name} {isInvalid ? 'Test Failed' : 'Test Passed'}
                                      </span>
                                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getSeverityColor(guardrail.severity)}`}>
                                        {guardrail.severity}
                                      </span>
                                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${getCategoryColor(guardrail.category)}`}>
                                        {guardrail.category}
                                      </span>
                                    </div>
                                    {isInvalid && (
                                      <div className="mt-2 space-y-1">
                                        {guardrail.violation_message && (
                                          <p className="text-sm text-red-800 dark:text-red-200">
                                            {guardrail.violation_message}
                                          </p>
                                        )}
                                        {guardrail.detected_at && (
                                          <p className="text-xs text-gray-600 dark:text-gray-400">
                                            Detected: {formatDate(guardrail.detected_at)}
                                          </p>
                                        )}
                                        <div className="flex gap-2 mt-2 flex-wrap">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEvaluationModal({
                                                scanId,
                                                resourceId: resource.resource_id,
                                                guardrailId: guardrail.guardrail_id,
                                                guardrailName: guardrail.guardrail_name,
                                              });
                                            }}
                                            className="flex items-center gap-1"
                                          >
                                            <Eye className="w-4 h-4" />
                                            View Evaluation Details
                                          </Button>
                                          {guardrail.violation_id && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/cloudops/guardrails/violations/${guardrail.violation_id}`);
                                              }}
                                            >
                                              View Violation Details
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {!isInvalid && (
                                      <div className="mt-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEvaluationModal({
                                              scanId,
                                              resourceId: resource.resource_id,
                                              guardrailId: guardrail.guardrail_id,
                                              guardrailName: guardrail.guardrail_name,
                                            });
                                          }}
                                          className="flex items-center gap-1"
                                        >
                                          <Eye className="w-4 h-4" />
                                          View Evaluation Details
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Info className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No resources match the selected filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evaluation Details Modal */}
      <EvaluationDetailsModal
        isOpen={!!evaluationModal}
        onClose={() => setEvaluationModal(null)}
        scanId={evaluationModal?.scanId || ''}
        resourceId={evaluationModal?.resourceId || ''}
        guardrailId={evaluationModal?.guardrailId || ''}
        guardrailName={evaluationModal?.guardrailName}
      />
    </div>
  );
};

export default ScanDetailsTimelinePage;

